import React, { useContext } from 'react'
import { View, Text } from 'styles/reactnative'
import { ThemeContext } from 'theme/ThemeProvider'
import BigNumber from 'bignumber.js'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useTranslation } from 'react-i18next'
import { useMyProfile } from '../../hooks/user/myprofile'
import CIUtils from '../../../utils/ciutils'
import { CardContainer, CardHeader, CardBody, BrandLogo, ProfitAmount, InfoRow, InfoLabel, InfoValue, UidBadge, TimeStamp } from './styles'

dayjs.extend(utc)
dayjs.extend(timezone)

interface ProfitProofCardProps {
	brandName: string
	brandUrl: string
	symbol: string
	codename?: string
	name?: string
	longShort: 'L' | 'S'
	profitAmount: number
	entryPrice: number
	exitPrice?: number
	amount: number
	tradedAt?: string
	isPnl?: boolean // true면 평가손익, false면 실현손익
	dp?: number
}

const ProfitProofCard = React.forwardRef<HTMLDivElement, ProfitProofCardProps>(
	(
		{
			brandName,
			brandUrl,
			symbol,
			codename,
			name,
			longShort,
			profitAmount,
			entryPrice,
			exitPrice,
			amount,
			tradedAt,
			isPnl = false,
			dp
		},
		ref
	) => {
		const { theme, isDarkMode } = useContext(ThemeContext)
		const { COLORS } = theme
		const { data: myinfo } = useMyProfile()
		const { t } = useTranslation()

		const isProfit = profitAmount > 0

		// 포인트 컬러 가져오기 (브랜드 구분용)
		const pointColor = (window as any).BRAND_THEME?.colors?.point || '#667eea'

		// name에서 뒤 7자리(-숫자6자리) 제거
		const displayName = name && name.length > 7 && /^-\d{6}$/.test(name.slice(-7))
			? name.slice(0, -7)
			: name

		const now = dayjs()

		return (
			<CardContainer ref={ref} isDarkMode={isDarkMode} pointColor={pointColor}>
				<CardHeader>
					<BrandLogo isDarkMode={isDarkMode} pointColor={pointColor}>
						{t('title')}
					</BrandLogo>
					<View style={{ alignItems: 'flex-end' }}>
						<TimeStamp isDarkMode={isDarkMode}>
							{now.format('YYYY-MM-DD HH:mm:ss')}
						</TimeStamp>
						<UidBadge isDarkMode={isDarkMode}>#{CIUtils.padId(myinfo?.id)}</UidBadge>
					</View>
				</CardHeader>

				<CardBody>
					<div style={{ marginBottom: 16, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
						<Text
							style={{
								fontSize: 20,
								color: !isDarkMode ? '#fff' : '#1e293b',
								marginBottom: 4,
								opacity: 1,
								fontWeight: '700',
								display: 'block',
								width: '100%',
								lineHeight: '1.3',
							}}>
							{codename ? `${codename} (${symbol})` : symbol}
						</Text>
						{displayName && (
							<Text
								style={{
									fontSize: 18,
									color: !isDarkMode ? '#fff' : '#1e293b',
									marginBottom: 8,
									opacity: 1,
									fontWeight: '600',
									display: 'block',
									width: '100%',
									lineHeight: '1.3',
								}}>
								{displayName}
							</Text>
						)}
						<ProfitAmount isProfit={isProfit}>
							{isProfit ? '+' : ''}
							{BigNumber(profitAmount).toFormat(0)} 원
						</ProfitAmount>
					</div>

					<div>
						<InfoRow isDarkMode={isDarkMode}>
							<InfoLabel isDarkMode={isDarkMode}>매매구분</InfoLabel>
							{exitPrice ? (

								<InfoValue isDarkMode={isDarkMode} isHighlight={true} color={longShort === 'S' ? '#ff7b7b' : '#62a7ff'}>
								{longShort === 'S' ? '매수' : '매도'}
								</InfoValue>
							):(
								
									<InfoValue isDarkMode={isDarkMode} isHighlight={true} color={longShort === 'L' ? '#ff7b7b' : '#62a7ff'}>
									{longShort === 'L' ? '매수' : '매도'}
								</InfoValue>
							)}
						</InfoRow>

						{exitPrice ? (
							<InfoRow isDarkMode={isDarkMode}>
								<InfoLabel isDarkMode={isDarkMode}>청산가</InfoLabel>
								<InfoValue isDarkMode={isDarkMode}>{BigNumber(exitPrice).toFormat(dp)}</InfoValue>
							</InfoRow>
						): (
							<InfoRow isDarkMode={isDarkMode}>
								<InfoLabel isDarkMode={isDarkMode}>진입가</InfoLabel>
								<InfoValue isDarkMode={isDarkMode}>{BigNumber(entryPrice).toFormat(dp)}</InfoValue>
							</InfoRow>

						)}

						<InfoRow isDarkMode={isDarkMode}>
							<InfoLabel isDarkMode={isDarkMode}>계약수</InfoLabel>
							<InfoValue isDarkMode={isDarkMode}>{BigNumber(amount).toFormat(0)}</InfoValue>
						</InfoRow>

						{!isPnl && (
							<InfoRow isDarkMode={isDarkMode}>
								<InfoLabel isDarkMode={isDarkMode}>체결일</InfoLabel>
								<InfoValue isDarkMode={isDarkMode}>
									{tradedAt
										? dayjs(tradedAt).utc().tz('Asia/Seoul').add(9, 'hours').format('YYYY-MM-DD HH:mm:ss')
										: '-'}
								</InfoValue>
							</InfoRow>
						)}
					</div>
				</CardBody>
			</CardContainer>
		)
	}
)

ProfitProofCard.displayName = 'ProfitProofCard'

export default ProfitProofCard
